import { useQuery, useMutation } from '@tanstack/react-query';
import {
	getCreditSimulationList,
	CreditSimulationListParams,
	CreditSimulationListResponse,
	searchFinancingLimit,
	SearchFinancingLimitParams,
	SearchFinancingLimitResponse,
	getSimulationFacilities,
	GetSimulationFacilitiesParams,
	GetSimulationFacilitiesResponse,
	calculateSimulation,
	CalculateSimulationRequest,
	getSearchOptions,
	GetSearchOptionsResponse,
	getSimulationDetail,
	GetSimulationDetailRequest,
	GetSimulationDetailResponse,
	downloadSimulation,
	DownloadSimulationRequest,
	SaveSimulationResponse,
	saveSimulationCalculation,
	calculateFiduciaryFee,
	CalculateFiduciaryFeeRequest,
	CalculateServicingFeeRequest,
	calculateServicingFee,
	CalculatePHJMBAmountRequest,
	calculatePHJMBAmount,
	GetSimulationHistoryDetailRequest,
	getSimulationHistoryDetail,
	editSimulationCalculation,
	SimulationEditProductResponse,
	SimulationEditProductRequest,
	editSimulationProduct,
	CalculateSimulationResponse,
	getCalculatedSimulation,
	GetCalculatedSimulationRequest,
} from '../services/simulation';

export const useCreditSimulationList = (params: CreditSimulationListParams) => {
	return useQuery<CreditSimulationListResponse>({
		queryKey: ['getCreditSimulationList', params],
		queryFn: async () => {
			const res = await getCreditSimulationList(params);
			return res.data;
		},
	});
};

export const useSearchFinancingLimit = (params: SearchFinancingLimitParams) => {
	return useQuery<SearchFinancingLimitResponse>({
		queryKey: ['searchFinancingLimit', params],
		queryFn: async () => {
			const res = await searchFinancingLimit(params);
			return res.data;
		},
	});
};

export const useSimulationFacilities = (
	params: GetSimulationFacilitiesParams,
) => {
	return useQuery<GetSimulationFacilitiesResponse>({
		queryKey: ['getSimulationFacilities', params],
		queryFn: async () => {
			const res = await getSimulationFacilities(params);
			return res.data;
		},
	});
};

export const useCalculateSimulation = () =>
	useMutation({
		mutationFn: (body: CalculateSimulationRequest) =>
			calculateSimulation(body).then((res) => res.data),
	});

export const useGetCalculateSimulation = (
	params: GetCalculatedSimulationRequest,
) => {
	return useQuery<CalculateSimulationResponse>({
		queryKey: ['getCalculatedSimulation'],
		queryFn: async () => {
			const res = await getCalculatedSimulation(params);
			return res.data;
		},
		enabled: !!params.simulationId,
	});
};

export const useSearchOptions = () => {
	return useQuery<GetSearchOptionsResponse>({
		queryKey: ['getSearchOptions'],
		queryFn: async () => {
			const res = await getSearchOptions();
			return res.data;
		},
	});
};

export const useSimulationDetail = (
	params: GetSimulationDetailRequest | GetSimulationHistoryDetailRequest,
) => {
	return useQuery<GetSimulationDetailResponse>({
		/**
		 * The simplest and most robust query key is one that includes all parameters.
		 * TanStack Query will serialize the object to create a unique key.
		 * This avoids manual and error-prone key construction.
		 */
		queryKey: ['simulationDetail', params],

		/**
		 * The type guard ('in' operator) works perfectly inside the query function
		 * to help TypeScript determine which API call to make.
		 */
		queryFn: async () => {
			if ('simulationId' in params && params.simulationId) {
				// If simulationId exists, TypeScript knows `params` is GetSimulationHistoryDetailRequest
				const res = await getSimulationHistoryDetail(params);
				return res.data;
			}
			// Otherwise, it must be a GetSimulationDetailRequest.
			// We can cast it to satisfy TypeScript, as our `enabled` logic protects this.
			const res = await getSimulationDetail(
				params as GetSimulationDetailRequest,
			);
			return res.data;
		},

		/**
		 * The `enabled` condition is now fully type-safe. It uses the `in` operator
		 * to check for the existence of properties before trying to access their values.
		 * This allows the query to run only when one of the valid parameter shapes is provided.
		 */
		enabled:
			('simulationId' in params && !!params.simulationId) ||
			('spfId' in params &&
				'facilityId' in params &&
				'calculationTypeId' in params &&
				!!params.spfId &&
				!!params.facilityId &&
				params.calculationTypeId != null),
	});
};
export const useDownloadSimulation = () =>
	useMutation({
		mutationFn: (body: DownloadSimulationRequest) =>
			downloadSimulation(body).then((res) => res.data),
	});

export const useSaveSimulationCalculation = () =>
	useMutation<SaveSimulationResponse, unknown, CalculateSimulationRequest>({
		mutationFn: (body) =>
			saveSimulationCalculation(body).then((res) => res.data),
	});

export const useEditSimulationCalculation = () =>
	useMutation<
		SaveSimulationResponse,
		unknown,
		CalculateSimulationRequest & { simulationId: number }
	>({
		mutationFn: (body) =>
			editSimulationCalculation(body).then((res) => res.data),
	});

export const useEditSimulationProduct = () =>
	useMutation<
		SimulationEditProductResponse,
		unknown,
		SimulationEditProductRequest
	>({
		mutationFn: (body) => editSimulationProduct(body).then((res) => res.data),
	});

export const useCalculateFiduciaryFee = () =>
	useMutation<number, unknown, CalculateFiduciaryFeeRequest>({
		mutationFn: (body) =>
			calculateFiduciaryFee(body).then((res) => res.data.fiduciaryFee),
	});

export const useCalculateServicingFee = () =>
	useMutation<number, unknown, CalculateServicingFeeRequest>({
		mutationFn: (body) =>
			calculateServicingFee(body).then((res) => res.data.servicingFee),
	});

export const useCalculatePHJMBAmount = () =>
	useMutation<number, unknown, CalculatePHJMBAmountRequest>({
		mutationFn: (body) =>
			calculatePHJMBAmount(body).then((res) => res.data.phjmbAmount),
	});
